import { isPortalUser, buildPortalUrl } from './portalLink';
import { resolvePrimaryRole } from './rolePrecedence';
import { isSafeInternalPath } from './platformRoutes';
import { workspacePath } from '../workspace/utils/workspacePaths';

function websiteReturnPath(res: LoginResponse, requested: string | undefined, fallback: string): string {
  if (!isSafeInternalPath(requested)) return fallback;
  const role = res.auth_routing?.primary_role ?? resolvePrimaryRole(res.user) ?? fallback.split('/')[1];
  if (role && ['landlord', 'agent', 'agency'].includes(role)) {
    const scoped = workspacePath(requested, `/${role}`);
    return scoped.startsWith(`/${role}/`) ? scoped : fallback;
  }
  return /^\/(tenant(?:\/|\?|$)|checkout(?:\?|$)|payments\/recover\/)/.test(requested) ? requested : fallback;
}

/** Backend-authoritative routing block returned by /auth/login, /auth/me, etc. */
export interface SubscriptionOnboardingRouting {
  v33_enabled?: boolean;
  required?: boolean;
  path?: string | null;
  onboarding_status?: string | null;
}

export interface AuthRouting {
  primary_role?: string | null;
  app?: 'portal' | 'website';
  allowed_apps?: string[];
  base_url?: string;
  default_path?: string;
  redirect_url?: string;
  subscription_onboarding?: SubscriptionOnboardingRouting;
}

export interface LoginResponse {
  user?: { roles?: Array<{ name?: string; role_name?: string }> } | null;
  auth_routing?: AuthRouting;
}

export interface PostLoginTarget {
  /** true = full-page navigation to another app; false = in-app React Router navigation. */
  external: boolean;
  url: string;
}

/**
 * Decide where to send a user after a successful login.
 *
 * The backend `auth_routing.app` decision is the single source of truth for
 * role → application routing. The destination URL is built from the website's
 * OWN environment config (buildPortalUrl) so each environment redirects to the
 * correct portal host. Falls back to legacy local role mapping only when the
 * backend field is absent (e.g. an older API during rollout).
 */
export function resolvePostLoginTarget(res: LoginResponse, returnPath?: string): PostLoginTarget {
  const onboarding = res.auth_routing?.subscription_onboarding;
  if (onboarding?.required && onboarding.path) {
    const app = res.auth_routing?.app;
    if (app === 'portal') {
      return { external: true, url: buildPortalUrl(onboarding.path) };
    }
    return { external: false, url: onboarding.path };
  }

  const app = res.auth_routing?.app;

  if (app === 'portal') {
    const requested = isSafeInternalPath(returnPath) && !/^\/(tenant|landlord|agent|agency)(?:\/|$)/.test(returnPath) ? returnPath : undefined;
    return { external: true, url: buildPortalUrl(requested ?? res.auth_routing?.default_path ?? '/dashboard') };
  }
  if (app === 'website') {
    const fallback = res.auth_routing?.default_path ?? '/tenant/dashboard';
    return { external: false, url: websiteReturnPath(res, returnPath, fallback) };
  }

  // Fallback: legacy local role mapping (kept for backward compatibility).
  if (res.user && isPortalUser(res.user)) {
    return { external: true, url: buildPortalUrl(returnPath ?? '/dashboard') };
  }
  const primary = resolvePrimaryRole(res.user);
  const fallback = primary && ['landlord', 'agent', 'agency'].includes(primary) ? `/${primary}/dashboard` : '/tenant/dashboard';
  return { external: false, url: websiteReturnPath(res, returnPath, fallback) };
}

/**
 * Given an /auth/me payload, decide whether the current (website) application is
 * allowed for the user. Returns the portal path to redirect to when the website
 * is NOT an allowed app (so the caller sends them to the portal); otherwise null.
 *
 * Backend `allowed_apps` is authoritative. Returns null when the field is absent
 * (older API / rollout) so existing behaviour is preserved.
 */
/** Paths where we must not auto-redirect away during post-signup subscription onboarding. */
export const SUBSCRIPTION_ONBOARDING_ALLOWED_PATHS = [
  '/subscription/choose-plan',
  '/subscription/signup-checkout',
  '/checkout',
  '/login',
  '/confirm-identity',
  '/create-account',
] as const;

export function isSubscriptionOnboardingAllowedPath(pathname: string): boolean {
  const localPath = pathname.replace(/^\/(landlord|agent|agency)(?=\/)/, '');
  return SUBSCRIPTION_ONBOARDING_ALLOWED_PATHS.some((p) => localPath.startsWith(p));
}

/**
 * Website-only: navigate internally when subscription selection is still required.
 * Returns the path to open, or null when no redirect is needed.
 */
export function websiteSubscriptionOnboardingPath(
  me: { auth_routing?: AuthRouting } | null | undefined,
  pathname: string,
): string | null {
  const routing = me?.auth_routing;
  if (routing?.app !== 'website') return null;
  const onboarding = routing.subscription_onboarding;
  if (!onboarding?.required || !onboarding.path) return null;
  if (isSubscriptionOnboardingAllowedPath(pathname)) return null;
  if (pathname === onboarding.path) return null;
  return onboarding.path;
}

export function websiteRedirectFor(
  me: { auth_routing?: AuthRouting } | null | undefined,
): string | null {
  const routing = me?.auth_routing;
  const allowed = routing?.allowed_apps;
  if (!routing || !Array.isArray(allowed) || allowed.length === 0) return null;
  if (allowed.includes('website')) return null;
  return routing.default_path ?? '/dashboard';
}
